import { isElement, isDocument } from '../utils/dom';
import { Child, LocalPath, Path, OffsetType, CFIRange, Offset } from './types';
import { nodeToStep, nodeToOffset, IGNORE_ATTRIBUTE } from './common';
import { findIndex } from '../utils';

function step(node: Node): Child {
  return new Child(
    nodeToStep(node),
    isElement(node) && node.hasAttribute('id') ? node.getAttribute('id')! : undefined,
  );
}

export function nodeToLocalPath(
  node: Node,
  root: Node | null = node.ownerDocument && node.ownerDocument.documentElement,
): LocalPath {
  const result: Child[] = [];
  if (root === null) {
    return new LocalPath();
  }

  for (let target = node as Node | null;
       target !== null && !isDocument(target) && target !== root;
       target = target.parentNode) {
    const parent = target.parentNode!;
    if (isElement(parent) && parent.hasAttribute(IGNORE_ATTRIBUTE)) {
      continue;
    }
    result.unshift(step(target));
  }
  return new LocalPath(...result);
}

export function positionToPath(
  node: Node,
  offset: number,
  root: Node | null = node.ownerDocument && node.ownerDocument.documentElement,
): Path {
  if (root === null) {
    return new Path();
  }

  if (isElement(node)) {
    if (node.childNodes.length === offset) {
      const steps = nodeToLocalPath(node, root).children.slice();
      if (steps.length !== 0) {
        const last = steps[steps.length - 1];
        steps[steps.length - 1] = new Child(last.value + 1, last.id);
        const localPath = new LocalPath(...steps);
        return new Path([localPath]);
      }
      return new Path([new LocalPath()]);
    }
    return new Path([nodeToLocalPath(node.childNodes[offset], root)]);
  }
  return new Path(
    [nodeToLocalPath(node, root)],
    new Offset({ type: OffsetType.CHARACTER, offset: nodeToOffset(node) + offset }),
  );
}

export function rangeToCFI(range: Range): CFIRange {
  let {
    startContainer,
    startOffset,
    endContainer,
    endOffset,
    commonAncestorContainer: root,
  } = range;
  while (isElement(root) && root.hasAttribute(IGNORE_ATTRIBUTE)) {
    root = root.parentNode!;
  }
  if (isDocument(root)) {
    const { documentElement } = root;
    const index = findIndex(root.childNodes, value => value === documentElement);
    if (startContainer === root && documentElement !== null) {
      startContainer = documentElement;
      startOffset = index >= startOffset ? 0 : startContainer.childNodes.length;
    }
    if (endContainer === root && documentElement !== null) {
      endContainer = documentElement;
      endOffset = index >= endOffset ? 0 : endContainer.childNodes.length;
    }
    if (root.documentElement !== null) {
      root = root.documentElement;
    }
  }
  return new CFIRange([nodeToLocalPath(root)],
                      positionToPath(startContainer, startOffset, root),
                      positionToPath(endContainer, endOffset, root));
}

export * from './common';
export * from './types';
export * from './parsers';
